import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { admin } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Save, Building2, MapPin, Phone, Mail, Globe, FileText, Loader2, CreditCard, Link2, Copy, Trash2, Plus, Eye, Wrench } from 'lucide-react';
import { previewLinks as previewLinksApi } from '../../lib/api';

const AdminSettings = () => {
  usePageTitle('Settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLinks, setPreviewLinks] = useState([]);
  const [previewNote, setPreviewNote] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [repairing, setRepairing] = useState(false);
  
  const [settings, setSettings] = useState({
    business_name: '',
    tagline: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    website: '',
    logo_url: '',
    gst_number: '',
    abn: '',
    // Payment settings
    online_payments_enabled: true,
    payment_fee_percentage: 3.2,
    payment_fee_fixed: 0.30,
    pass_fees_to_customer: true
  });

  useEffect(() => {
    loadSettings();
    loadPreviewLinks();
  }, []);

  const loadPreviewLinks = async () => {
    try {
      const res = await previewLinksApi.getAll();
      setPreviewLinks(res.data);
    } catch {}
  };

  const createPreviewLink = async () => {
    setCreatingLink(true);
    try {
      await previewLinksApi.create(previewNote);
      setPreviewNote('');
      toast.success('Preview link created');
      loadPreviewLinks();
    } catch {
      toast.error('Failed to create link');
    } finally {
      setCreatingLink(false);
    }
  };

  const revokePreviewLink = async (token) => {
    try {
      await previewLinksApi.revoke(token);
      toast.success('Preview link revoked');
      loadPreviewLinks();
    } catch {
      toast.error('Failed to revoke link');
    }
  };

  const copyPreviewLink = (token) => {
    const url = `${window.location.origin}/preview/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Preview link copied to clipboard');
  };

  const handleRepairInventorySizes = async () => {
    if (!window.confirm('This will scan every product and remove inventory rows whose size does not match the product\'s sizing type (e.g., a "Kids" product with S/M/L rows). Missing kids-size rows will be added with quantity 0. Continue?')) {
      return;
    }
    setRepairing(true);
    try {
      const res = await admin.repairInventorySizes();
      const count = res.data.repaired_count || 0;
      if (count === 0) {
        toast.success('All products are already in good shape. Nothing to repair.');
      } else {
        toast.success(`Repaired ${count} product${count === 1 ? '' : 's'}. Refresh product pages to see the correct sizes.`);
      }
    } catch (e) {
      toast.error('Repair failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRepairing(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await admin.getBusinessSettings();
      setSettings({
        ...settings,
        ...response.data,
        online_payments_enabled: response.data.online_payments_enabled ?? true,
        payment_fee_percentage: response.data.payment_fee_percentage ?? 3.2,
        payment_fee_fixed: response.data.payment_fee_fixed ?? 0.30,
        pass_fees_to_customer: response.data.pass_fees_to_customer ?? true
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await admin.updateBusinessSettings(settings);
      toast.success('Business settings updated');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 w-1/4 rounded" />
          <div className="h-[400px] bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
          <p className="text-gray-600">Manage your business information</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#002FA7]"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
          )}
        </Button>
      </div>

      <div className="space-y-8">
        {/* Business Identity */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Business Identity</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={settings.business_name || ''}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="Your Business Name"
                className="mt-1"
              />
            </div>
            
            <div className="col-span-2 md:col-span-1">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={settings.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Your business tagline"
                className="mt-1"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                value={settings.logo_url || ''}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                className="mt-1"
              />
              {settings.logo_url && (
                <div className="mt-2">
                  <img 
                    src={settings.logo_url} 
                    alt="Logo preview" 
                    className="h-12 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Phone className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={settings.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@yourbusiness.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </Label>
              <Input
                id="phone"
                value={settings.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="mt-1"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="website">
                <Globe className="w-4 h-4 inline mr-1" />
                Website
              </Label>
              <Input
                id="website"
                value={settings.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://www.yourbusiness.com"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Business Address</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={settings.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Business Street"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={settings.city || ''}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="City"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="state">State / Province</Label>
              <Input
                id="state"
                value={settings.state || ''}
                onChange={(e) => handleChange('state', e.target.value)}
                placeholder="State"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input
                id="postal_code"
                value={settings.postal_code || ''}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="12345"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={settings.country || ''}
                onChange={(e) => handleChange('country', e.target.value)}
                placeholder="Country"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Tax & Registration */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Tax & Registration</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gst_number">GST Number</Label>
              <Input
                id="gst_number"
                value={settings.gst_number || ''}
                onChange={(e) => handleChange('gst_number', e.target.value)}
                placeholder="GST123456789"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="abn">ABN (Australian Business Number)</Label>
              <Input
                id="abn"
                value={settings.abn || ''}
                onChange={(e) => handleChange('abn', e.target.value)}
                placeholder="12 345 678 901"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Payment Settings</h2>
          </div>
          
          <div className="space-y-6">
            {/* Enable/Disable Online Payments */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Online Payments</Label>
                <p className="text-sm text-gray-500">Allow customers to pay their invoices online via PayPal</p>
              </div>
              <Switch
                checked={settings.online_payments_enabled}
                onCheckedChange={(checked) => handleChange('online_payments_enabled', checked)}
              />
            </div>
            
            {/* Pass Fees to Customer */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Pass Fees to Customer</Label>
                <p className="text-sm text-gray-500">Add processing fees to customer's payment (you receive the full amount)</p>
              </div>
              <Switch
                checked={settings.pass_fees_to_customer}
                onCheckedChange={(checked) => handleChange('pass_fees_to_customer', checked)}
              />
            </div>
            
            {/* Fee Settings */}
            {settings.pass_fees_to_customer && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <Label htmlFor="fee_percentage">Fee Percentage (%)</Label>
                  <Input
                    id="fee_percentage"
                    type="number"
                    step="0.1"
                    value={settings.payment_fee_percentage || 2.9}
                    onChange={(e) => handleChange('payment_fee_percentage', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">PayPal typically charges 2.6% - 2.9%</p>
                </div>
                
                <div>
                  <Label htmlFor="fee_fixed">Fixed Fee (AUD)</Label>
                  <Input
                    id="fee_fixed"
                    type="number"
                    step="0.01"
                    value={settings.payment_fee_fixed || 0.30}
                    onChange={(e) => handleChange('payment_fee_fixed', parseFloat(e.target.value))}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">PayPal typically charges $0.30 per transaction</p>
                </div>
                
                <div className="col-span-2 mt-2 p-3 bg-white rounded border">
                  <p className="text-sm font-medium text-gray-700">Example Fee Calculation:</p>
                  <p className="text-sm text-gray-600">
                    For a $100 invoice, customer pays: <strong>${((100 + (settings.payment_fee_fixed || 0.30)) / (1 - (settings.payment_fee_percentage || 2.9) / 100)).toFixed(2)}</strong>
                  </p>
                  <p className="text-xs text-gray-500">You receive the full $100 after PayPal fees</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="border rounded-lg p-6 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
          <div className="bg-white p-4 rounded border">
            <div className="flex items-center gap-3 mb-2">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="h-8 object-contain" />
              ) : (
                <Building2 className="w-8 h-8 text-[#002FA7]" />
              )}
              <div>
                <h3 className="font-bold text-[#002FA7]">{settings.business_name || 'Business Name'}</h3>
                <p className="text-sm text-gray-500">{settings.tagline || 'Tagline'}</p>
              </div>
            </div>
            <div className="text-sm text-gray-600 space-y-1 mt-4">
              <p>{settings.address}, {settings.city}, {settings.state} {settings.postal_code}</p>
              <p>{settings.country}</p>
              <p>{settings.phone} • {settings.email}</p>
              {settings.gst_number && <p>GST: {settings.gst_number}</p>}
              {settings.abn && <p>ABN: {settings.abn}</p>}
            </div>
          </div>
        </div>

        {/* Preview Links */}
        <div className="border rounded-lg p-6" data-testid="preview-links-section">
          <div className="flex items-center gap-3 mb-4">
            <Link2 className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-gray-900">Preview Links</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Generate shareable links for prospective customers to browse your catalogue without seeing prices. Each link is locked to the first device that opens it.
          </p>

          {/* Create New */}
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Note (e.g. customer name)"
              value={previewNote}
              onChange={(e) => setPreviewNote(e.target.value)}
              className="flex-1"
              data-testid="preview-link-note-input"
            />
            <Button
              onClick={createPreviewLink}
              disabled={creatingLink}
              className="bg-[#002FA7] shrink-0"
              data-testid="generate-preview-link-btn"
            >
              {creatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Generate Link</>}
            </Button>
          </div>

          {/* Links List */}
          {previewLinks.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No preview links generated yet</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {previewLinks.map((link) => (
                <div
                  key={link.token}
                  className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded border text-sm"
                  data-testid={`preview-link-item-${link.token}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Eye className="w-3.5 h-3.5 text-[#64748B] shrink-0" />
                      <span className="font-mono text-xs text-[#0F172A] truncate">/preview/{link.token}</span>
                      {link.device_id && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Claimed</span>
                      )}
                    </div>
                    {link.note && <p className="text-xs text-gray-500 mt-0.5 ml-5">{link.note}</p>}
                    <p className="text-[10px] text-gray-400 ml-5 mt-0.5">
                      Created {new Date(link.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyPreviewLink(link.token)}
                      className="h-8 w-8 p-0"
                      title="Copy link"
                      data-testid={`copy-preview-link-${link.token}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revokePreviewLink(link.token)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      title="Revoke link"
                      data-testid={`revoke-preview-link-${link.token}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Maintenance Tools */}
        <div className="bg-white rounded-lg shadow-sm p-6" data-testid="maintenance-tools-card">
          <div className="flex items-center gap-2 mb-4">
            <Wrench className="w-5 h-5 text-[#002FA7]" />
            <h2 className="text-lg font-semibold text-[#0F172A]">Maintenance Tools</h2>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-medium text-[#0F172A] mb-1">Repair inventory sizes</h3>
            <p className="text-sm text-[#64748B] mb-3">
              Scans every product and removes inventory rows whose size does not match its sizing type.
              Allowed sizes per type: <span className="font-mono text-xs">unisex</span> = S, M, L, XL, XXL ·{' '}
              <span className="font-mono text-xs">ladyfit</span> = S, M, L, XL ·{' '}
              <span className="font-mono text-xs">onesize</span> = OS ·{' '}
              <span className="font-mono text-xs">kids</span> = 1-2 → 11-12 ·{' '}
              <span className="font-mono text-xs">combined</span> = S/M, L/XL.
              Missing sizes are added at qty 0. Use this if products show wrong size badges on the storefront.
            </p>
            <Button
              onClick={handleRepairInventorySizes}
              disabled={repairing}
              variant="outline"
              data-testid="repair-inventory-sizes-btn"
            >
              {repairing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Repairing...
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Run repair
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
